import React, { useEffect, createContext, useState } from "react";
import allowedUserIds from "../pages/admin/adminConfig";
import axios from "axios";
import Env from "../runtimeEnv";
import axiosInstance from "../api/axiosInstance";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentNickname, setCurrentNickname] = useState("");
  const [PlutonicuserID, setPlutonicuserID] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userType, setUserType] = useState("");
  const [verifiedUserAccess, setVerifiedUserAccess] = useState(null);
  const [accessVerificationComplete, setAccessVerificationComplete] = useState(false);
  const [currentFormType, setCurrentFormType] = useState("login");
  const [loading, setLoading] = useState(true);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [email, setEmail] = useState(localStorage.getItem("email") || "");
  const [surveyCompleted, setSurveyCompleted] = useState(
    localStorage.getItem("surveyCompleted") === "true" // 👈 read from storage
  );
  const [selectedTherapyType, setSelectedTherapyType] = useState("");

  const verifyUserAccessFromServer = async () => {
    try {
      const response = await axiosInstance.get(
        `${Env.REACT_APP_PYTHON_API_URL}/api/verify-user-access`,
        {
          withCredentials: true
        }
      );
      
      if (response.data.hasAccess) {
        setUserType(response.data.userType);
        setVerifiedUserAccess(response.data.access);
        setAccessVerificationComplete(true);
        
        return true;
      } else {
        console.warn("AuthContext.js: warn event");
        setAccessVerificationComplete(true);
      }
    } catch (error) {
      console.error("AuthContext.js: error event");
      setUserType("");
      setVerifiedUserAccess(null);
      setAccessVerificationComplete(true);
      
      if (error.response?.status === 401) {
        console.warn("AuthContext.js: warn event");

        await handleLogout();
        return false;
      }
    }
    
    return false;
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const storedNickname = localStorage.getItem("currentNickname");
      const storedUserID = localStorage.getItem("PlutonicuserID");
      const storedUserType = localStorage.getItem("userType");
      const storedEmail = localStorage.getItem("email");

      if (storedUserID) {
        if(storedNickname) setCurrentNickname(storedNickname);
        setPlutonicuserID(storedUserID);
        setIsAuthenticated(true);
        setIsAdmin(allowedUserIds.includes(storedUserID));
        
        if (storedUserType) {
          setUserType(storedUserType);
        }
        
        await verifyUserAccessFromServer();
      }

      if (storedEmail) {
        setEmail(storedEmail);
      }

      setLoading(false);
    };

    initializeAuth();
  }, []);

  const handleLogout = async () => {
    try {
      const meetingID = sessionStorage.getItem("meetingID");
      const PlutonicuserID = localStorage.getItem("PlutonicuserID");

      if (meetingID && PlutonicuserID) {
        try {
          await axios.post(
            `${Env.REACT_APP_PYTHON_API_URL}/api/set-online-status/${PlutonicuserID}`,
            { meetingID, online: false },
            { withCredentials: true }
          );
        } catch (statusError) {
          console.warn('Could not update online status during logout.');
        }
      }

      // console.log('[App] Logging out from Cognito...');
      await axios.post(`${Env.REACT_APP_PYTHON_API_URL}/api/logout`, {}, { withCredentials: true });
    } catch (err) {
      if (err.response?.status === 500) {
        console.warn("AuthContext.js: warn event");
      } else {
        console.error("AuthContext.js: error event");
      }
    } finally {
      // console.log('[App] Clearing session and local storage.');
      localStorage.clear();
      sessionStorage.clear();
      setCurrentNickname("");
      setPlutonicuserID("");
      setEmail("");
      setCurrentFormType("login");
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUserType("");
      setVerifiedUserAccess(null);
      setAccessVerificationComplete(false); 
      setSelectedTherapyType("");
      setLoading(false); 
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentNickname,
        setCurrentNickname,
        PlutonicuserID,
        setPlutonicuserID,
        isAuthenticated,
        setIsAuthenticated,
        isAdmin,
        setIsAdmin,
        email,
        setEmail,
        userType,
        setUserType,
        currentFormType,
        setCurrentFormType,
        loading,
        setLoading,
        surveyCompleted,
        setSurveyCompleted,
        showNewPasswordForm,
        setShowNewPasswordForm,
        selectedTherapyType,
        setSelectedTherapyType,
        verifiedUserAccess,
        accessVerificationComplete,
        verifyUserAccessFromServer,
        handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
